import { initializeApp, FirebaseApp } from 'firebase/app';
import { Auth, getAuth } from 'firebase/auth';
import { Analytics, getAnalytics, logEvent } from 'firebase/analytics';

const DEV_BYPASS = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true';
const hasApiKey = !!process.env.REACT_APP_FIREBASE_API_KEY;

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Skip real Firebase init when dev-bypass is on or no API key is present.
// AuthProvider supplies a mock user in that mode and the backend accepts
// 'dev-bypass-token' — `auth` here is a stub for any stray imports.
const shouldInit = !DEV_BYPASS && hasApiKey;

let app: FirebaseApp | null = null;
let auth: Auth;
// Exported as Analytics for callers (useAuth.ts logEvent) — guarded below and
// never fired on the dev-bypass path.
let analytics: Analytics = null as unknown as Analytics;

if (shouldInit) {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  analytics = getAnalytics(app);
} else {
  if (!DEV_BYPASS) {
    console.warn('[firebase] REACT_APP_FIREBASE_API_KEY not set; skipping initialization');
  }
  auth = new Proxy({} as Auth, {
    get(_target, prop) {
      if (prop === 'currentUser') return null;
      if (prop === 'onAuthStateChanged') return (_cb: any) => () => {};
      return () => {
        throw new Error(`Firebase auth stub invoked (${String(prop)}): dev bypass active`);
      };
    },
  });
}

export const logAnalyticsEvent = (eventName: string, eventParams?: Record<string, any>) => {
  if (!analytics) return;
  try {
    logEvent(analytics, eventName, eventParams);
  } catch (error) {
    console.error('Error logging analytics event:', error);
  }
};

export { auth, analytics };
export default app;
